import { Box, Chip as CountBadge, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SurgicalCaseEntity } from '@peritasai/repository';
import { Chip } from '@peritasai/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CASE_CART_QUEUE_STAGE, isDispatchedCartStatus } from '../utils/queues';
import { CaseCartAssemblyRow } from './CaseCartAssemblyRow';

dayjs.extend(advancedFormat);

const UPCOMING_DATE_CHIPS_SHOWN = 3;

const cardWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
};

const queuesWrapSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '16px',
};

const headerRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
};

const cardTitleSx = {
  color: '#121b2a',
  fontSize: '32px',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.32px',
};

const dateChipGroupSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
};

const sectionWrapSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
};

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const sectionTitleSx = {
  color: '#121b2a',
  fontSize: '22px',
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: '-1.1px',
};

const countBadgeSx = {
  height: '25px',
  fontSize: '10px',
  fontWeight: 700,
  borderRadius: '8px',
  backgroundColor: '#e3e3e3',
  color: '#121b2a',
  '& .MuiChip-label': { px: '8px' },
};

const cardGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridAutoRows: '259px',
  gap: '16px',
  width: '100%',
  maxWidth: '1220px',
};

const emptyStateSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 16px',
  borderRadius: '12px',
  background: '#fff',
  border: '1px dashed rgba(0, 0, 0, 0.12)',
};

const emptyMessageSx = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontSize: '14px',
};

type CaseCartAssemblyCardProps = {
  readyToBuild: SurgicalCaseEntity[];
  readyForDispatch: SurgicalCaseEntity[];
  arrivedInOr: SurgicalCaseEntity[];
  now: Date;
  selectedDate: Dayjs;
  onSelectDate: (date: Dayjs) => void;
  onSelect: (caseId: string) => void;
  onSendToOr: (caseId: string) => void;
  sendingToOrCaseId: string | null;
};

export const CaseCartAssemblyCard = ({
  readyToBuild,
  readyForDispatch,
  arrivedInOr,
  now,
  selectedDate,
  onSelectDate,
  onSelect,
  onSendToOr,
  sendingToOrCaseId,
}: CaseCartAssemblyCardProps) => {
  const { t } = useTranslation(['periverse']);
  const theme = useTheme();

  const selectedDateChipSx = {
    '& .MuiChip-label': {
      padding: '0 6px !important',
    },
    '&.MuiChip-selected, &.MuiChip-selected:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.main,
      '& .MuiChip-label': {
        color: theme.palette.primary.contrastText,
      },
    },
  };

  const dateChipOptions = useMemo(
    () =>
      Array.from({ length: UPCOMING_DATE_CHIPS_SHOWN }, (_, index) =>
        dayjs(now).add(index, 'day').startOf('day')
      ),
    [now]
  );

  return (
    <Box sx={cardWrapSx} data-testid="case-cart-assembly-card">
      {/* Page header: title + date chips */}
      <Box sx={headerRowSx}>
        <Typography sx={cardTitleSx}>
          {t('pages.caseCartAssembly.title')}
        </Typography>
        <Box sx={dateChipGroupSx}>
          {dateChipOptions.map((date, index) => {
            const isSelected = date.isSame(selectedDate, 'day');
            return (
              <Chip
                key={date.format('YYYY-MM-DD')}
                variant="outlined"
                color="primary"
                clickable
                selected={isSelected}
                sx={selectedDateChipSx}
                label={
                  index === 0
                    ? t('pages.buildCart.caseList.todayChipLabel')
                    : date.format('Do MMM')
                }
                onClick={() => onSelectDate(date)}
                data-testid={
                  index === 0
                    ? 'case-list-today-chip'
                    : `case-list-date-chip-${date.format('YYYY-MM-DD')}`
                }
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={queuesWrapSx}>
        {/* Ready to Build queue */}
        <Box sx={sectionWrapSx}>
          <Box sx={sectionHeaderSx}>
            <Typography sx={sectionTitleSx}>
              {t('pages.caseCartAssembly.readyToBuildTitle')}
            </Typography>
            <CountBadge
              label={readyToBuild.length}
              size="small"
              sx={countBadgeSx}
            />
          </Box>
          {readyToBuild.length === 0 ? (
            <Box sx={emptyStateSx} data-testid="ready-to-build-empty">
              <Typography sx={emptyMessageSx}>
                {t('pages.caseCartAssembly.noReadyToBuild')}
              </Typography>
            </Box>
          ) : (
            <Box sx={cardGridSx} data-testid="ready-to-build-grid">
              {readyToBuild.map((caseItem) => (
                <CaseCartAssemblyRow
                  key={caseItem.id}
                  caseItem={caseItem}
                  now={now}
                  queueStage={CASE_CART_QUEUE_STAGE.READY_TO_BUILD}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Ready for Dispatch queue — both awaiting-send and already-dispatched cards */}
        <Box sx={sectionWrapSx}>
          <Box sx={sectionHeaderSx}>
            <Typography sx={sectionTitleSx}>
              {t('pages.caseCartAssembly.readyForDispatchTitle')}
            </Typography>
            <CountBadge
              label={readyForDispatch.length}
              size="small"
              sx={countBadgeSx}
            />
          </Box>
          {readyForDispatch.length === 0 ? (
            <Box sx={emptyStateSx} data-testid="ready-for-dispatch-empty">
              <Typography sx={emptyMessageSx}>
                {t('pages.caseCartAssembly.noReadyForDispatch')}
              </Typography>
            </Box>
          ) : (
            <Box sx={cardGridSx} data-testid="ready-for-dispatch-grid">
              {readyForDispatch.map((caseItem) => (
                <CaseCartAssemblyRow
                  key={caseItem.id}
                  caseItem={caseItem}
                  now={now}
                  queueStage={
                    isDispatchedCartStatus(caseItem.cartCompletenessStatus)
                      ? CASE_CART_QUEUE_STAGE.DISPATCHED
                      : CASE_CART_QUEUE_STAGE.READY_FOR_DISPATCH
                  }
                  onSelect={onSelect}
                  onSendToOr={onSendToOr}
                  isSendingToOr={sendingToOrCaseId === caseItem.id}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Arrived in OR queue */}
        <Box sx={sectionWrapSx}>
          <Box sx={sectionHeaderSx}>
            <Typography sx={sectionTitleSx}>
              {t('pages.caseCartAssembly.arrivedInOrTitle')}
            </Typography>
            <CountBadge
              label={arrivedInOr.length}
              size="small"
              sx={countBadgeSx}
            />
          </Box>
          {arrivedInOr.length === 0 ? (
            <Box sx={emptyStateSx} data-testid="arrived-in-or-empty">
              <Typography sx={emptyMessageSx}>
                {t('pages.caseCartAssembly.noArrivedInOr')}
              </Typography>
            </Box>
          ) : (
            <Box sx={cardGridSx} data-testid="arrived-in-or-grid">
              {arrivedInOr.map((caseItem) => (
                <CaseCartAssemblyRow
                  key={caseItem.id}
                  caseItem={caseItem}
                  now={now}
                  queueStage={CASE_CART_QUEUE_STAGE.ARRIVED_IN_OR}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
